import { Fragment, useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FiSend, FiArrowLeft, FiUser, FiUsers, FiX, FiMoreVertical, FiUnlock, FiMessageSquare, FiSmile, FiCornerUpLeft, FiPhoneOff, FiCheck } from "react-icons/fi";
import { useSocket } from "../context/SocketContext";
import { useTranslation } from "../context/TranslationContext";
import { getMessages, sendMessage, sendMessageAttachment, messageFileUrl, markAsRead, blockUser, unblockUser, checkBlocked, reactToMessage, respondToMessageRequest } from "../api/messages";
import { endSupportConversation } from "../api/mintSpace";
import UserProfileLink from "./UserProfileLink";
import { formatLastSeen } from "../utils/lastSeen";
import { getProfileAvatarUrl } from "../utils/userDisplay";
import { requestState } from "../utils/messageRequests";
import { filesFrom, messagePreview } from "../utils/chatFiles";
import useChatFiles from "../hooks/useChatFiles";
import ChatAttachMenu from "./ChatAttachMenu";
import ChatAttachment from "./ChatAttachment";
import ChatPendingFiles from "./ChatPendingFiles";
import GroupAvatar from "./GroupAvatar";
import GroupInfoModal from "./GroupInfoModal";
import Icon from "./ds/Icon";
import MentionSuggestions from "./MentionSuggestions";
import { eventText, isGroupChat, memberSummary, mentionableMembers, personName } from "../utils/groupChats";
import { mentionAtCaret, splitMentions } from "../utils/mentions";
/** The small chat prints the time again after a pause this long. */
const TIME_GAP_MS = 15 * 60 * 1000;

const clockTime = (dateString) => {
  const date = new Date(dateString);
  const time = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (date.toDateString() === new Date().toDateString()) return time;
  return `${date.toLocaleDateString([], { month: "short", day: "numeric" })} · ${time}`;
};

const ChatWindow = ({
  conversation,
  currentUser,
  isDark,
  setToast,
  onBack,
  peerUserOverride = null,
  skipBlockCheck = false,
  mintSpaceId = null,
  onSupportConversationUpdated = null,
  onRestartSupport = null,
  /** Compact height for floating dock */
  floating = false,
  /** Fills its parent, for Messages' two panels: the list is beside it on desktop. */
  fill = false,
  /** Optional close (X) when floating */
  onClose = null,
  /** Called with ("accept" | "reject", response data) after this user answers a message request here */
  onRequestAnswered = null,
  /** A group's name, people or admins changed (here or by someone else): the group as it now is. */
  onGroupChanged = null,
  /** This person left the group, or was removed from it. */
  onGroupLeft = null,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [hasBlockedUs, setHasBlockedUs] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [reactingTo, setReactingTo] = useState(null);
  /** Photos, videos and files picked for the next messages. */
  const chatFiles = useChatFiles({ onError: setToast });
  const [draggingFiles, setDraggingFiles] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  /** After switching conversation or first open — always jump to latest message */
  const initialScrollPendingRef = useRef(true);
  const typingTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const inputEmojiPickerRef = useRef(null);
  const inputRef = useRef(null);
  const messageRefs = useRef({});
  const { socket, connected } = useSocket();

  const isGroup = isGroupChat(conversation);
  // A group as it now is: its name and people change while it's open.
  const [group, setGroup] = useState(isGroup ? conversation : null);
  const [groupInfoOpen, setGroupInfoOpen] = useState(false);
  /** Removed from the group, or left it, while it was open. */
  const [notInGroup, setNotInGroup] = useState(false);
  const [typingUserId, setTypingUserId] = useState(null);
  /** An "@name" being typed in a group's message box: where it starts, and what's typed after "@". */
  const [mention, setMention] = useState(null);
  const [mentionActive, setMentionActive] = useState(0);
  useEffect(() => {
    setGroup(isGroupChat(conversation) ? conversation : null);
    setNotInGroup(false);
    setGroupInfoOpen(false);
  }, [conversation?._id]);
  useEffect(() => {
    if (isGroupChat(conversation)) setGroup((prev) => ({ ...prev, ...conversation }));
  }, [conversation?.name, conversation?.participants?.length, String(conversation?.moderator), (conversation?.admins || []).join()]);
  const groupChanged = (next) => {
    setGroup((prev) => ({ ...prev, ...next }));
    onGroupChanged?.(next);
  };

  const hideBlockingUi = skipBlockCheck || conversation?.kind === "support" || isGroup;
  const isSupportChat = conversation?.kind === "support";
  const isSupportEnded = conversation?.status === "ended";
  const [endingSupport, setEndingSupport] = useState(false);

  const currentId = (currentUser.id || currentUser._id)?.toString();

  // A chat still waiting as a message request. Kept here too so an answer shows at once,
  // wherever the chat is open.
  const [request, setRequest] = useState(conversation?.request || null);
  const [answeringRequest, setAnsweringRequest] = useState(false);
  useEffect(() => {
    setRequest(conversation?.request || null);
  }, [conversation?._id, conversation?.status, conversation?.request?.status]);
  const requestStatus = requestState({ ...conversation, request }, currentId);
  /** The other side answers a request before replying; a declined sender can't write. */
  const canWrite = !["received", "youDeclined", "declined"].includes(requestStatus) && !notInGroup;
  const otherParticipant = useMemo(() => {
    if (peerUserOverride) return peerUserOverride;
    if (isGroupChat(conversation)) return null;
    if (!conversation?.participants) return null;
    const inParticipants = conversation.participants.some(
      (p) => (p._id || p)?.toString() === currentId
    );
    if (conversation.kind === "support" && !inParticipants && conversation.requester) {
      const rid = (conversation.requester._id || conversation.requester).toString();
      const fromParticipants = conversation.participants.find(
        (p) => (p._id || p)?.toString() === rid
      );
      if (fromParticipants) return fromParticipants;
    }
    return conversation.participants.find((p) => {
      const pid = (p._id || p)?.toString();
      return pid && pid !== currentId;
    });
  }, [conversation, currentId, peerUserOverride]);

  // Common emojis for quick reactions
  const commonEmojis = ["👍", "❤️", "😂", "😮", "😢", "🙏", "🔥", "💯"];

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";
  const inputBase =
    "bg-fill-tertiary text-label-primary outline-none placeholder:text-label-tertiary focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]";

  /** Figma "Chat window": the conversation, in 360 by 560 */
  const fc = floating
    ? {
        root: "h-full max-h-full rounded-radius-lg",
        header: "gap-space-8 border-b px-space-8 py-space-12",
        headerIconBtn: "flex size-8 shrink-0 items-center justify-center rounded-full",
        headerIcon: "h-[18px] w-[18px]",
        avatar: "size-8 rounded-14",
        avatarLetter: "text-caption-1-semibold",
        title: "text-headline",
        typing: "text-footnote",
        reconnect: "px-2 py-0.5 text-caption-1",
        msgScroll: "space-y-space-8 overflow-x-hidden overflow-y-auto p-space-16 chat-scrollbar",
        emptyIconWrap: "mb-3 p-4",
        emptyIcon: "h-8 w-8",
        emptyTitle: "mb-1 text-headline",
        emptySub: "text-footnote",
        bubbleWidth: "max-w-[78%]",
        bubbleText: "text-subheadline whitespace-pre-wrap break-words",
        metaTime: "mt-space-4 text-caption-1",
        typingBubble: "max-w-[75%] rounded-radius-md px-3 py-2",
        replyBar: "px-3 py-2",
        footer: "!border-t-0 px-space-12 pb-space-12 pt-space-8",
        formRow: "gap-space-4 rounded-full border border-separator-default bg-fill-tertiary p-space-8",
        emojiBtn: "flex size-8 shrink-0 items-center justify-center rounded-full",
        emojiIcon: "h-[18px] w-[18px]",
        input: "order-1 !bg-transparent px-space-4 py-[5px] text-subheadline focus-visible:!shadow-none",
        attach: "order-2",
        sendBtn: "order-3 flex size-8 shrink-0 items-center justify-center rounded-full",
        sendIcon: "h-4 w-4",
        moreBtn: "p-2",
      }
    : {
        root: fill ? "lg:rounded-radius-lg" : "rounded-radius-lg",
        header: "gap-space-12 border-b py-space-12 pl-space-24 pr-space-16",
        headerIconBtn: "rounded-radius-sm p-2.5",
        headerIcon: "h-5 w-5",
        avatar: "size-12 rounded-14",
        avatarLetter: "text-headline",
        title: "text-headline",
        typing: "text-footnote",
        reconnect: "text-footnote px-3 py-1.5",
        msgScroll: "space-y-space-8 overflow-x-hidden overflow-y-auto p-space-24 pb-space-16 chat-scrollbar",
        emptyIconWrap: "mb-4 p-6",
        emptyIcon: "h-12 w-12",
        emptyTitle: "text-headline mb-1",
        emptySub: "text-subheadline",
        bubbleWidth: "max-w-[75%]",
        bubbleText: "text-subheadline whitespace-pre-wrap break-words",
        metaTime: "mt-space-4 text-caption-1",
        typingBubble: "max-w-[70%] rounded-radius-md px-4 py-2",
        replyBar: "px-4 py-2",
        footer: "p-4",
        formRow: "gap-3",
        emojiBtn: "rounded-radius-sm p-3",
        emojiIcon: "h-5 w-5",
        input: "rounded-[22px] px-space-16 py-[10px] text-callout",
        attach: "",
        sendBtn: "flex size-11 shrink-0 items-center justify-center rounded-full",
        sendIcon: "h-5 w-5",
        moreBtn: "p-2.5",
      };

  // Check blocked status (skipped for support threads — no blocking)
  useEffect(() => {
    if (hideBlockingUi) {
      setIsBlocked(false);
      setHasBlockedUs(false);
      return;
    }
    const checkBlockedStatus = async () => {
      if (!otherParticipant?._id) return;
      try {
        const response = await checkBlocked(otherParticipant._id);
        if (response.success) {
          setIsBlocked(response.data.isBlocked);
          setHasBlockedUs(response.data.hasBlockedUs);
        }
      } catch (error) {
        console.error("Error checking blocked status:", error);
      }
    };

    checkBlockedStatus();
  }, [otherParticipant?._id, hideBlockingUi]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
        setReactingTo(null);
      }
      if (inputEmojiPickerRef.current && !inputEmojiPickerRef.current.contains(event.target)) {
        setEmojiPickerOpen(false);
      }
    };

    if (dropdownOpen || emojiPickerOpen || reactingTo) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen, emojiPickerOpen, reactingTo]);

  // Scroll to message function
  const scrollToMessage = (messageId) => {
    const messageElement = messageRefs.current[messageId];
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });
      // Highlight briefly
      messageElement.classList.add("ring-2", "ring-accent-default");
      setTimeout(() => {
        messageElement.classList.remove("ring-2", "ring-accent-default");
      }, 2000);
    }
  };

  useEffect(() => {
    initialScrollPendingRef.current = true;
    chatFiles.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation?._id]);

  // Fetch messages
  useEffect(() => {
    const fetchMessages = async () => {
      if (!conversation?._id) return;
      try {
        setLoading(true);
        setMessages([]);
        const response = await getMessages(conversation._id);
        if (response.success) {
          setMessages(response.data || []);
          // Mark as read
          await markAsRead(conversation._id);
        }
      } catch (error) {
        console.error("Error fetching messages:", error);
        if (setToast) {
          setToast(t("messages.failedToLoadMessages") || "Failed to load messages");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [conversation?._id, setToast]);

  // Socket event listeners
  useEffect(() => {
    if (!socket || !conversation?._id) return;

    const handleNewMessage = (data) => {
      if (data.conversationId === conversation._id && data.message) {
        const mid = data.message._id;
        setMessages((prev) => {
          if (prev.some((m) => m._id === mid)) return prev;
          return [...prev, data.message];
        });
        markAsRead(conversation._id);
      }
    };

    const handleSupportNewMessage = (data) => {
      if (conversation.kind !== "support") return;
      if (data.conversationId !== conversation._id || !data.message) return;
      const mid = data.message._id;
      setMessages((prev) => {
        if (prev.some((m) => m._id === mid)) return prev;
        return [...prev, data.message];
      });
      markAsRead(conversation._id);
    };

    const handleMessageDelivered = (data) => {
      if (data.conversationId === conversation._id) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === data.messageId ? { ...msg, status: "delivered" } : msg
          )
        );
      }
    };

    const handleMessagesRead = (data) => {
      if (data.conversationId === conversation._id) {
        const sid = (currentUser.id || currentUser._id)?.toString();
        setMessages((prev) =>
          prev.map((msg) => {
            const senderId = (msg.sender?._id || msg.sender)?.toString();
            return data.messageIds.includes(msg._id) && senderId === sid
              ? { ...msg, status: "read", readAt: new Date() }
              : msg;
          })
        );
      }
    };

    const handleTyping = (data) => {
      if (data.conversationId === conversation._id && data.userId !== currentUser.id) {
        setIsTyping(data.isTyping);
        setTypingUserId(data.isTyping ? data.userId : null);
        if (data.isTyping) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => {
            setIsTyping(false);
          }, 3000);
        }
      }
    };

    const handleMessageReacted = (data) => {
      if (data.conversationId === conversation._id) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === data.messageId ? data.message : msg
          )
        );
      }
    };

    // The other side answered this chat's message request
    const answeredAs = (status) => (data) => {
      setRequest((prev) =>
        prev && String(prev._id) === String(data.requestId) ? { ...prev, status } : prev
      );
    };
    const handleRequestAccepted = answeredAs("accepted");
    const handleRequestRejected = answeredAs("rejected");

    socket.on("new_message", handleNewMessage);
    socket.on("support_new_message", handleSupportNewMessage);
    socket.on("message_delivered", handleMessageDelivered);
    socket.on("messages_read", handleMessagesRead);
    socket.on("typing", handleTyping);
    socket.on("message_reacted", handleMessageReacted);
    socket.on("message_request_accepted", handleRequestAccepted);
    socket.on("message_request_rejected", handleRequestRejected);

    // A group's name or people changed, or this person is no longer in it.
    const handleGroupUpdated = ({ conversation: next }) => {
      if (next && String(next._id) === String(conversation._id)) setGroup((prev) => ({ ...prev, ...next }));
    };
    const handleGroupRemoved = ({ conversationId }) => {
      if (String(conversationId) === String(conversation._id)) setNotInGroup(true);
    };
    socket.on("group_updated", handleGroupUpdated);
    socket.on("group_removed", handleGroupRemoved);

    return () => {
      socket.off("new_message", handleNewMessage);
      socket.off("support_new_message", handleSupportNewMessage);
      socket.off("message_delivered", handleMessageDelivered);
      socket.off("messages_read", handleMessagesRead);
      socket.off("typing", handleTyping);
      socket.off("message_reacted", handleMessageReacted);
      socket.off("message_request_accepted", handleRequestAccepted);
      socket.off("message_request_rejected", handleRequestRejected);
      socket.off("group_updated", handleGroupUpdated);
      socket.off("group_removed", handleGroupRemoved);
    };
  }, [socket, conversation?._id, conversation?.kind, currentUser.id]);

  // Keep view pinned to the latest messages: jump to bottom on open/load, then only if user is already near the bottom
  useEffect(() => {
    if (loading) return;

    const scrollToBottom = () => {
      const container = messagesContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      } else {
        messagesEndRef.current?.scrollIntoView({ block: "end", behavior: "auto" });
      }
    };

    const run = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const container = messagesContainerRef.current;
          if (initialScrollPendingRef.current) {
            scrollToBottom();
            initialScrollPendingRef.current = false;
            setTimeout(scrollToBottom, 0);
            setTimeout(scrollToBottom, 120);
            return;
          }
          if (!container) {
            scrollToBottom();
            return;
          }
          const { scrollTop, scrollHeight, clientHeight } = container;
          const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
          const nearBottom = distanceFromBottom < 140;
          if (nearBottom) {
            scrollToBottom();
          }
        });
      });
    };

    run();
  }, [messages, isTyping, loading]);

  // The list shrinks when a phone's keyboard opens: stay on the latest message if it was in view.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (loading || !container || !window.ResizeObserver) return undefined;
    let height = container.clientHeight;
    const observer = new ResizeObserver(() => {
      const shrunkBy = height - container.clientHeight;
      height = container.clientHeight;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (shrunkBy > 0 && scrollHeight - scrollTop - clientHeight - shrunkBy < 140) {
        container.scrollTop = scrollHeight;
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [loading]);

  // Handle typing indicator
  const handleTypingChange = (isTypingValue) => {
    if (!socket || !conversation?._id || isSupportEnded) return;
    
    setTyping(isTypingValue);
    socket.emit("typing", {
      conversationId: conversation._id,
      isTyping: isTypingValue,
    });

    if (isTypingValue) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        setTyping(false);
        socket.emit("typing", {
          conversationId: conversation._id,
          isTyping: false,
        });
      }, 3000);
    }
  };

  const handleEndSupport = async () => {
    if (!mintSpaceId || !conversation?._id || isSupportEnded) return;
    if (!window.confirm(t("mintSpace.confirmEndSupport") || "End this support conversation?")) return;
    setEndingSupport(true);
    try {
      const res = await endSupportConversation(mintSpaceId, conversation._id);
      if (res.success && res.data) {
        if (setToast) setToast(t("mintSpace.supportConversationEnded") || "Conversation ended");
        if (typeof onSupportConversationUpdated === "function") onSupportConversationUpdated(res.data);
        setDropdownOpen(false);
      }
    } catch (error) {
      console.error("Error ending support:", error);
      if (setToast) {
        setToast(
          error?.response?.data?.message ||
            error?.message ||
            t("mintSpace.endSupportFailed") ||
            "Could not end conversation"
        );
      }
    } finally {
      setEndingSupport(false);
    }
  };

  const handleBlockUser = async () => {
    if (hideBlockingUi) return;
    if (!otherParticipant?._id) return;
    try {
      const response = await blockUser(otherParticipant._id);
      if (response.success) {
        setIsBlocked(true);
        setDropdownOpen(false);
        if (setToast) {
          setToast(t("messages.userBlockedSuccess") || "User blocked successfully");
        }
      }
    } catch (error) {
      console.error("Error blocking user:", error);
      if (setToast) {
        setToast(error.response?.data?.message || t("messages.failedToBlock") || "Failed to block user");
      }
    }
  };

  const handleUnblockUser = async () => {
    if (!otherParticipant?._id) return;
    try {
      const response = await unblockUser(otherParticipant._id);
      if (response.success) {
        setIsBlocked(false);
        if (setToast) {
          setToast(t("messages.userUnblockedSuccess") || "User unblocked successfully");
        }
      }
    } catch (error) {
      console.error("Error unblocking user:", error);
      if (setToast) {
        setToast(error.response?.data?.message || t("messages.failedToUnblock") || "Failed to unblock user");
      }
    }
  };

  const handleReactToMessage = async (messageId, emoji) => {
    try {
      const response = await reactToMessage(messageId, emoji);
      if (response.success) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === messageId ? response.data : msg
          )
        );
        setReactingTo(null);
        // Emit socket event
        if (socket) {
          socket.emit("message_reacted", {
            conversationId: conversation._id,
            messageId: messageId,
            message: response.data,
          });
        }
      }
    } catch (error) {
      console.error("Error reacting to message:", error);
      if (setToast) {
        setToast(t("messages.failedToReact") || "Failed to react to message");
      }
    }
  };

  const handleAnswerRequest = async (action) => {
    if (!request?._id || answeringRequest) return;
    try {
      setAnsweringRequest(true);
      const response = await respondToMessageRequest(request._id, action);
      if (response.success) {
        setRequest((prev) => ({ ...prev, status: action === "accept" ? "accepted" : "rejected" }));
        if (setToast) {
          setToast(
            action === "accept"
              ? t("messages.requestAccepted") || "Message request accepted"
              : t("messages.requestRejected") || "Message request rejected"
          );
        }
        if (typeof onRequestAnswered === "function") onRequestAnswered(action, response.data);
      }
    } catch (error) {
      console.error("Error answering message request:", error);
      if (setToast) {
        setToast(
          error.response?.data?.message ||
            (action === "accept"
              ? t("messages.failedToAccept") || "Failed to accept request"
              : t("messages.failedToReject") || "Failed to reject request")
        );
      }
    } finally {
      setAnsweringRequest(false);
    }
  };

  /** A message the server took: show it, and tell the other side live. */
  const delivered = (message) => {
    setMessages((prev) => (prev.some((m) => m._id === message._id) ? prev : [...prev, message]));
    if (socket) {
      socket.emit("new_message", {
        conversationId: conversation._id,
        message,
      });
    }
  };

  const sendError = (error, fallback) => {
    if (setToast) {
      const errorMsg = error.response?.data?.message || fallback;
      setToast(errorMsg);
      if (errorMsg.includes("blocked")) {
        setIsBlocked(true);
      }
    }
    // Declined while this chat was open, before the news arrived
    if (error.response?.data?.code === "message_request_declined") {
      setRequest((prev) => (prev ? { ...prev, status: "rejected" } : prev));
    }
  };

  /** Each picked file as its own message, the typed text as the first one's caption. */
  const handleSendFiles = async () => {
    const caption = newMessage.trim();
    const replyId = replyingTo?._id || null;
    setSending(true);
    try {
      const done = await chatFiles.sendAll(async (file, index, onProgress) => {
        try {
          const response = await sendMessageAttachment(conversation._id, file, {
            content: index === 0 ? caption : "",
            replyTo: index === 0 ? replyId : null,
            onProgress,
          });
          if (!response.success) return false;
          delivered(response.data);
          if (index === 0) {
            setNewMessage("");
            setReplyingTo(null);
          }
          return true;
        } catch (error) {
          console.error("Error sending file:", error);
          sendError(error, t("chatFiles.sendError", { name: file.name }));
          return false;
        }
      });
      if (done) {
        setTyping(false);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    } finally {
      setSending(false);
    }
  };

  // Only the group's members are suggested, as only they can be mentioned.
  const mentionPeople = isGroup && mention ? mentionableMembers(group, currentId, mention.query) : [];
  const trackMention = (input) =>
    setMention(isGroup ? mentionAtCaret(input.value, input.selectionStart ?? input.value.length) : null);
  const pickMention = (member) => {
    const input = inputRef.current;
    if (!mention || !member) return;
    const caret = input?.selectionStart ?? newMessage.length;
    const insert = `@${member.username} `;
    const position = mention.start + insert.length;
    setNewMessage(newMessage.slice(0, mention.start) + insert + newMessage.slice(caret));
    setMention(null);
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(position, position);
    });
  };
  /** Who a message mentions, by name: ones that came as bare ids are named from the group. */
  const mentionedIn = (message) =>
    (message.mentions || []).map((m) =>
      m?.username ? m : (group?.participants || []).find((p) => String(p?._id ?? p) === String(m)) || m
    );

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (isSupportEnded || !canWrite) return;
    if (sending || !conversation?._id || isBlocked || hasBlockedUs) return;
    if (chatFiles.pending.length) return handleSendFiles();
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      const response = await sendMessage(conversation._id, newMessage.trim(), replyingTo?._id || null);
      
      if (response.success) {
        delivered(response.data);
        setNewMessage("");
        setMention(null);
        setReplyingTo(null);
        setTyping(false);
        
        // Auto-refocus input
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      sendError(error, t("messages.failedToSend") || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return t("messages.justNow") || "Just now";
    if (minutes < 60) return t("messages.minutesAgo", { minutes }) || `${minutes}m ago`;
    if (hours < 24) return t("messages.hoursAgo", { hours }) || `${hours}h ago`;
    if (days < 7) return t("messages.daysAgo", { days }) || `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "read":
        return "✓✓";
      case "delivered":
        return "✓✓";
      case "sent":
        return "✓";
      default:
        return "";
    }
  };

  const containerRef = useRef(null);

  /** Files dropped on the chat, or pasted into the box, join the picked ones. */
  const canAttach = !isSupportEnded && !isBlocked && !hasBlockedUs && canWrite;
  const dropHandlers = canAttach
    ? {
        onDragOver: (e) => {
          if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
          e.preventDefault();
          if (!draggingFiles) setDraggingFiles(true);
        },
        onDragLeave: (e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setDraggingFiles(false);
        },
        onDrop: (e) => {
          const dropped = filesFrom(e.dataTransfer);
          setDraggingFiles(false);
          if (!dropped.length) return;
          e.preventDefault();
          chatFiles.addFiles(dropped);
          inputRef.current?.focus();
        },
      }
    : {};
  const hasPending = chatFiles.pending.length > 0;
  const canSend = (hasPending || newMessage.trim()) && connected && !sending;

  const otherAvatarUrl = getProfileAvatarUrl(otherParticipant);
  const otherName = otherParticipant?.username || otherParticipant?.businessName || "User";

  if (loading) {
    return (
      <div
        className={`flex h-full items-center justify-center border ${cardBase} ${
          floating ? "rounded-radius-sm" : "rounded-radius-lg"
        }`}
      >
        <div className="text-center">
          <div
            className={`mb-4 inline-block animate-spin rounded-full border-4 border-solid border-accent-default border-r-transparent ${
              floating ? "mb-2 h-6 w-6 border-2" : "h-8 w-8"
            }`}
          />
          <p className={`${mutedText} ${floating ? "text-footnote" : ""}`}>
            {t("messages.loadingMessages") || "Loading messages..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col overflow-hidden ${floating ? `border ${cardBase} shadow-overlay` : "bg-bg-raised"} relative animate-slide-in-up ${
        floating ? "" : "backdrop-blur-sm"
      } ${fc.root} ${
        floating
          ? "h-full max-h-full min-w-0"
          : fill
          ? "h-full min-h-0"
          : "lg:h-[1000px] lg:max-h-[90vh] h-[calc(100vh-200px)] max-h-[calc(100vh-200px)]"
      }`}
      ref={containerRef}
      {...dropHandlers}
    >
      {isGroup && (
        <GroupInfoModal
          open={groupInfoOpen}
          conversation={group}
          currentUser={currentUser}
          onClose={() => setGroupInfoOpen(false)}
          onChanged={groupChanged}
          onLeft={() => {
            setGroupInfoOpen(false);
            setNotInGroup(true);
            onGroupLeft?.(conversation._id);
          }}
          isDark={isDark}
        />
      )}
      {draggingFiles && (
        <div className="pointer-events-none absolute inset-2 z-40 grid place-items-center rounded-radius-md border-2 border-dashed border-accent-default bg-ink/70 text-subheadline-semibold text-accent-text backdrop-blur-sm">
          {t("chatFiles.dropHere")}
        </div>
      )}
      {/* Header */}
      <div
        className={`flex flex-shrink-0 items-center border-b ${fc.header} ${
          floating
            ? cardBase
            : cardBase
        }`}
      >
        {onBack && (
          <button
            type="button"
            title={floating ? t("common.back") : undefined}
            onClick={onBack}
            className={`${fc.headerIconBtn} transition-colors hover:bg-fill-quaternary ${fill ? "lg:hidden" : ""}`}
          >
            {floating ? <Icon name="chevron-left" size={18} /> : <FiArrowLeft className={`${fc.headerIcon} ${baseText}`} />}
          </button>
        )}
        {isGroup ? (
          <button
            type="button"
            onClick={() => setGroupInfoOpen(true)}
            className="flex min-w-0 flex-1 items-center gap-3 text-left"
            title={t("groups.groupInfo")}
          >
            <GroupAvatar size={floating ? "md" : "lg"} isDark={isDark} className={floating ? "" : "h-12 w-12"} />
            <span className="min-w-0 flex-1">
              <span className={`block truncate ${fc.title} ${baseText}`}>{group?.name}</span>
              <span className={`block truncate ${fc.typing} ${mutedText}`}>
                {isTyping && typingUserId
                  ? t("groups.isTyping", {
                      name: personName(
                        (group?.participants || []).find((p) => String(p._id ?? p) === String(typingUserId)),
                        t
                      ),
                    })
                  : memberSummary(group, currentId, t)}
              </span>
            </span>
          </button>
        ) : (
          <>
        <div
          className={`flex-shrink-0 overflow-hidden ${floating ? "" : "rounded-full shadow-md"} ${fc.avatar} ${
            !hideBlockingUi && isBlocked ? "border-status-danger" : "border-accent-default"
          }`}
        >
          {otherAvatarUrl ? (
            <img
              src={otherAvatarUrl}
              alt={otherParticipant.username || otherParticipant.businessName}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className={`flex h-full w-full items-center justify-center ${
              floating ? "bg-fill-secondary" : isDark ? "bg-slate-700" : "bg-slate-200"
            }`}>
              <span
                className={`${fc.avatarLetter} text-label-secondary`}
              >
                {(otherParticipant?.username || otherParticipant?.businessName || "U").charAt(0).toUpperCase()}
              </span>
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <UserProfileLink
              user={otherParticipant}
              showAvatar={false}
              nameClassName={`${fc.title} ${baseText}`}
              className="min-w-0 truncate"
              fallback="User"
            />
            <span className="flex items-center gap-2 min-w-0">
            {isSupportEnded && (
              <span
                className={`text-footnote px-2 py-0.5 rounded-full shrink-0 ${
                  isDark ? "bg-slate-600/50 text-slate-200" : "bg-slate-200 text-slate-700"
                }`}
              >
                {t("mintSpace.supportEnded") || "Ended"}
              </span>
            )}
            {!hideBlockingUi && isBlocked && (
              <span className={`text-footnote px-2 py-0.5 rounded-full ${
                "bg-status-danger-bg text-status-danger"
              }`}>
                {t("messages.blocked") || "Blocked"}
              </span>
            )}
            {!hideBlockingUi && hasBlockedUs && (
              <span className={`text-footnote px-2 py-0.5 rounded-full ${
                "bg-status-warning-bg text-status-warning"
              }`}>
                {t("messages.blockedYou") || "Blocked you"}
              </span>
            )}
            </span>
          </div>
          {isTyping ? (
            <p className={`${fc.typing} ${mutedText}`}>{t("messages.typing") || "typing..."}</p>
          ) : floating && !connected ? (
            <p className="truncate text-footnote text-status-warning">{t("messages.reconnecting") || "Reconnecting..."}</p>
          ) : (
            floating && <p className={`truncate ${fc.typing} ${mutedText}`}>{formatLastSeen(otherParticipant?.lastSeen, t)}</p>
          )}
        </div>
          </>
        )}

        {mintSpaceId && isSupportChat && !isSupportEnded && (
          <button
            type="button"
            onClick={handleEndSupport}
            disabled={endingSupport}
            className={`flex shrink-0 items-center gap-1.5 rounded-radius-xs px-2 py-1 text-footnote-semibold transition sm:gap-2 sm:rounded-radius-sm sm:px-3 sm:py-2 sm:text-sm ${
              isDark
                ? "bg-white/10 text-slate-200 hover:bg-white/15"
                : "bg-slate-100 text-slate-800 hover:bg-slate-200"
            } disabled:opacity-50`}
          >
            <FiPhoneOff className={floating ? "h-3.5 w-3.5" : "h-4 w-4"} />
            <span className={floating ? "hidden min-[360px]:inline" : "hidden sm:inline"}>
              {t("mintSpace.endConversation") || "End conversation"}
            </span>
          </button>
        )}

        {/* Dropdown Menu */}
        <div className={`relative ${floating ? "hidden" : ""}`} ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className={`${fc.moreBtn} rounded-radius-sm transition-all ${
              dropdownOpen
                ? isDark
                  ? "bg-white/20"
                  : "bg-slate-200"
                : "hover:bg-fill-quaternary"
            }`}
          >
            <FiMoreVertical className={`${fc.headerIcon} ${baseText}`} />
          </button>
          
          {dropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setDropdownOpen(false)}
              />
              <div
                className={`absolute right-0 top-full mt-2 w-48 rounded-radius-md border shadow-2xl z-50 ${
                  "border-separator-default bg-bg-raised"
                }`}
              >
                <div className="p-2">
                  {isGroup ? (
                    <button
                      type="button"
                      onClick={() => {
                        setGroupInfoOpen(true);
                        setDropdownOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 rounded-radius-sm px-4 py-3 text-subheadline-semibold transition ${
                        "hover:bg-fill-quaternary text-label-primary"
                      }`}
                    >
                      <FiUsers className="h-4 w-4" />
                      {t("groups.groupInfo")}
                    </button>
                  ) : (
                  <button
                    type="button"
                    onClick={() => {
                      navigate(`/profile/${otherParticipant._id}`);
                      setDropdownOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 rounded-radius-sm px-4 py-3 text-subheadline-semibold transition ${
                      "hover:bg-fill-quaternary text-label-primary"
                    }`}
                  >
                    <FiUser className="h-4 w-4" />
                    {t("navigation.viewProfile") || "View Profile"}
                  </button>
                  )}
                  {!hideBlockingUi && !isBlocked ? (
                    <button
                      type="button"
                      onClick={handleBlockUser}
                      className={`w-full flex items-center gap-3 rounded-radius-sm px-4 py-3 text-subheadline-semibold transition ${
                        "hover:bg-fill-quaternary text-status-danger"
                      }`}
                    >
                      <FiX className="h-4 w-4" />
                      {t("messages.blockUser") || "Block User"}
                    </button>
                  ) : null}
                </div>
              </div>
            </>
          )}
        </div>

        {!connected && !floating && (
          <div
            className={`rounded-full ${fc.reconnect} ${
              "bg-status-warning-bg text-status-warning"
            }`}
          >
            {t("messages.reconnecting") || "Reconnecting..."}
          </div>
        )}
        {floating && onClose && (
          <>
            <button
              type="button"
              title={t("messages.openFullChat")}
              aria-label={t("messages.openFullChat")}
              onClick={() => {
                onClose();
                navigate(`/messages/${conversation._id}`);
              }}
              className={`${fc.headerIconBtn} transition-colors hover:bg-fill-quaternary`}
            >
              <Icon name="expand" size={18} />
            </button>
            <button
              type="button"
              title={t("messages.closeChat") || "Close chat"}
              onClick={onClose}
              className={`${fc.headerIconBtn} transition-colors hover:bg-fill-quaternary`}
              aria-label={t("messages.closeChat") || "Close chat"}
            >
              <Icon name="close" size={18} />
            </button>
          </>
        )}
      </div>

      {/* Messages - Scrollable Container */}
      <div
        ref={messagesContainerRef}
        className={`min-h-0 flex-1 ${fc.msgScroll} ${
          floating
            ? "bg-bg-raised"
            : ""
        }`}
      >
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center">
            <div
              className={`rounded-full ${
                "bg-fill-secondary"
              } ${fc.emptyIconWrap}`}
            >
              <FiMessageSquare className={`${fc.emptyIcon} ${mutedText}`} />
            </div>
            <p className={`${fc.emptyTitle} ${baseText}`}>{t("messages.noMessages")}</p>
            <p className={`${fc.emptySub} ${mutedText}`}>{t("messages.startConversation")}</p>
          </div>
        ) : (
          messages.map((message, index) => {
            const senderId = (m) => String(m?.sender?._id ?? m?.sender ?? "");
            // What happened in a group: a line across the chat, not a bubble.
            if (message.event?.type) {
              return (
                <div key={message._id} className="flex justify-center py-1">
                  <span
                    className={`max-w-[90%] rounded-full px-3 py-1 text-center ${floating ? "text-caption-1" : "text-footnote"} ${
                      "bg-fill-tertiary text-label-secondary"
                    }`}
                  >
                    {eventText(message, currentId, t)}
                  </span>
                </div>
              );
            }
            const isOwn = senderId(message) === currentId;
            const userReact = message.reacts?.find(r => r.user._id === currentUser.id);
            // In a group, a name over each run of someone else's messages.
            const prevMessage = messages[index - 1];
            const showSender =
              isGroup && !isOwn && (!prevMessage || prevMessage.event?.type || senderId(prevMessage) !== senderId(message));
            const senderName = showSender && (
              <p className={`mb-0.5 truncate text-footnote-semibold ${isDark ? "text-accent-text" : "text-status-success"}`}>
                {personName(message.sender, t)}
              </p>
            );
            // Photos and videos stand on their own: a bubble only holds what's written.
            const isMedia = ["image", "video"].includes(message.attachment?.kind);
            const hasBubble = !isMedia || Boolean(message.content || message.replyTo);
            const onMint = isOwn && hasBubble;
            // The small chat keeps times between messages, not on each one.
            const showTime =
              floating && (!prevMessage || new Date(message.createdAt) - new Date(prevMessage.createdAt) > TIME_GAP_MS);
            return (
              <Fragment key={message._id}>
              {showTime && <p className="text-center text-caption-1 text-label-tertiary">{clockTime(message.createdAt)}</p>}
              <div
                ref={(el) => {
                  if (el) messageRefs.current[message._id] = el;
                }}
                className={`flex ${isOwn ? "justify-end" : "justify-start"} group transition-all`}
              >
                <div
                  className={`relative flex min-w-0 flex-col gap-space-4 ${isOwn ? "items-end" : "items-start"} ${fc.bubbleWidth}`}
                >
                  {/* React/Reply buttons - show on hover */}
                  {/* On own messages: left side, on others: right side */}
                  {canWrite && (
                    <div className={`absolute ${isOwn ? "right-full mr-2" : "left-full ml-2"} top-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity`}>
                      <div className="relative" ref={reactingTo === message._id ? emojiPickerRef : null}>
                        <button
                          onClick={() => setReactingTo(reactingTo === message._id ? null : message._id)}
                          className={`p-2 rounded-full ${
                            "bg-bg-raised border border-separator-default"
                          } shadow-lg`}
                        >
                          <FiSmile className={`h-4 w-4 ${baseText}`} />
                        </button>
                        {reactingTo === message._id && (
                          <div className={`absolute ${isOwn ? "right-0" : "left-0"} top-full mt-2 p-3 rounded-radius-sm border shadow-2xl z-50 min-w-[200px] ${
                            "bg-bg-raised border-separator-default"
                          }`}>
                            <div className="grid grid-cols-4 gap-2">
                              {commonEmojis.map((emoji) => (
                                <button
                                  key={emoji}
                                  onClick={() => handleReactToMessage(message._id, emoji)}
                                  className="text-2xl hover:scale-125 transition-transform p-2 flex items-center justify-center"
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setReplyingTo(message);
                          inputRef.current?.focus();
                        }}
                        className={`p-2 rounded-full ${
                          "bg-bg-raised border border-separator-default"
                        } shadow-lg`}
                      >
                        <FiCornerUpLeft className={`h-4 w-4 ${baseText}`} />
                      </button>
                    </div>
                  )}
                  {isMedia && senderName}
                  {isMedia && (
                    <ChatAttachment
                      attachment={message.attachment}
                      url={messageFileUrl(message._id)}
                      downloadUrl={messageFileUrl(message._id, { download: true })}
                      own={isOwn}
                      isDark={isDark}
                      compact={floating}
                    />
                  )}
                  <div
                    className={`min-w-0 max-w-full ${
                      hasBubble
                        ? `rounded-[20px] px-space-16 py-space-8 ${isOwn ? "bg-accent-default text-accent-on" : "bg-fill-secondary text-label-primary"}`
                        : ""
                    }`}
                  >
                  {!isMedia && senderName}
                  {/* Reply Preview */}
                  {message.replyTo && (
                    <button
                      onClick={() => scrollToMessage(message.replyTo._id)}
                      className={`mb-2 pb-2 border-l-2 pl-2 w-full text-left hover:opacity-80 transition-opacity ${
                        isOwn ? "border-ink/30" : isDark ? "border-white/20" : "border-slate-300"
                      }`}
                    >
                      <p className={`text-footnote-semibold mb-1 ${
                        isOwn ? "text-accent-on opacity-70" : mutedText
                      }`}>
                        <UserProfileLink
                          user={message.replyTo.sender}
                          avatarSize="xs"
                          isDark={isDark}
                          stopPropagation
                          fallback="User"
                        />
                      </p>
                      <p className={`text-footnote truncate ${
                        isOwn ? "text-accent-on opacity-60" : mutedText
                      }`}>
                        {messagePreview(message.replyTo, t)}
                      </p>
                    </button>
                  )}
                  
                  {message.attachment?.kind && !isMedia && (
                    <div className={message.content ? "mb-1.5" : ""}>
                      <ChatAttachment
                        attachment={message.attachment}
                        url={messageFileUrl(message._id)}
                        downloadUrl={messageFileUrl(message._id, { download: true })}
                        own={isOwn}
                        isDark={isDark}
                        compact={floating}
                      />
                    </div>
                  )}
                  {message.content && (
                    <p className={fc.bubbleText}>
                      {isGroup && message.mentions?.length
                        ? splitMentions(message.content, mentionedIn(message)).map((part, i) =>
                            typeof part === "string" ? (
                              <span key={i}>{part}</span>
                            ) : (
                              <button
                                key={i}
                                type="button"
                                onClick={() => navigate(`/profile/${String(part.user._id)}`)}
                                className={`font-semibold hover:underline ${
                                  isOwn ? "underline decoration-ink/30 underline-offset-2" : isDark ? "text-accent-text" : "text-status-success"
                                } ${String(part.user._id) === currentId ? `rounded px-0.5 ${isOwn ? "bg-ink/10" : "bg-accent-tinted"}` : ""}`}
                              >
                                {part.text}
                              </button>
                            )
                          )
                        : message.content}
                    </p>
                  )}
                  
                  {/* Reacts */}
                  {message.reacts && message.reacts.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {Object.entries(
                        message.reacts.reduce((acc, react) => {
                          const emoji = react.emoji;
                          if (!acc[emoji]) acc[emoji] = [];
                          acc[emoji].push(react.user);
                          return acc;
                        }, {})
                      ).map(([emoji, users]) => (
                        <button
                          key={emoji}
                          onClick={() => handleReactToMessage(message._id, emoji)}
                          className={`text-footnote px-2 py-1 rounded-full border transition ${
                            userReact?.emoji === emoji
                              ? onMint
                                ? "bg-ink/20 border-ink/40"
                                : "bg-accent-tinted border-accent-default"
                              : onMint
                              ? "bg-ink/10 border-ink/20"
                              : "bg-bg-raised border-separator-default"
                          }`}
                        >
                          {emoji} {users.length}
                        </button>
                      ))}
                    </div>
                  )}
                  
                  <div
                    className={`items-center gap-2 ${floating ? "hidden" : "flex"} ${fc.metaTime} ${
                      onMint ? "text-accent-on opacity-70" : mutedText
                    }`}
                  >
                    <span>{formatTime(message.createdAt)}</span>
                    {isOwn && !isGroup && (
                      <span className="text-footnote font-medium">
                        {getStatusIcon(message.status)}
                      </span>
                    )}
                  </div>
                  </div>
                </div>
              </div>
              </Fragment>
            );
          })
        )}
        {isTyping && (
          <div className="flex justify-start">
            <div
              className={`${fc.typingBubble} ${
                "bg-fill-secondary"
              }`}
            >
              <div className="flex gap-1">
                <div className={`w-2 h-2 rounded-full ${
                  isDark ? "bg-slate-400" : "bg-slate-500"
                } animate-bounce`} style={{ animationDelay: "0ms" }}></div>
                <div className={`w-2 h-2 rounded-full ${
                  isDark ? "bg-slate-400" : "bg-slate-500"
                } animate-bounce`} style={{ animationDelay: "150ms" }}></div>
                <div className={`w-2 h-2 rounded-full ${
                  isDark ? "bg-slate-400" : "bg-slate-500"
                } animate-bounce`} style={{ animationDelay: "300ms" }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply Preview */}
      {replyingTo && (
        <div
          className={`flex flex-shrink-0 items-center justify-between border-t ${cardBase} ${fc.replyBar}`}
          style={{ marginTop: 0 }}
        >
          <div className="flex-1">
            <p className={`mb-1 flex flex-wrap items-baseline gap-1 text-footnote-semibold ${baseText}`}>
              <span>{t("messages.replyingToPrefix") || "Replying to"}</span>
              <UserProfileLink
                user={replyingTo.sender}
                avatarSize="xs"
                isDark={isDark}
                fallback="User"
              />
            </p>
            <p className={`text-footnote truncate ${mutedText}`}>
              {messagePreview(replyingTo, t)}
            </p>
          </div>
          <button
            onClick={() => setReplyingTo(null)}
            className={`p-1 rounded-radius-xs ${
              "hover:bg-fill-quaternary"
            }`}
          >
            <FiX className={`h-4 w-4 ${baseText}`} />
          </button>
        </div>
      )}

      {/* Input */}
      {isSupportEnded ? (
        <div
          className={`flex-shrink-0 border-t ${fc.footer} ${cardBase} ${
            floating
              ? ""
              : ""
          }`}
        >
          <p className={`${floating ? "mb-2 text-footnote font-medium" : "mb-3 text-subheadline font-medium"} ${baseText}`}>
            {t("mintSpace.supportConversationEndedMessage") ||
              "This support conversation has ended. You can start a new one when you need help again."}
          </p>
          {typeof onRestartSupport === "function" && (
            <button
              type="button"
              onClick={() => onRestartSupport()}
              className="rounded-radius-sm bg-accent-default px-5 py-2.5 text-subheadline-semibold text-accent-on"
            >
              {t("mintSpace.startSupportAgain") || "Start support again"}
            </button>
          )}
        </div>
      ) : notInGroup ? (
        <div className={`flex-shrink-0 border-t ${fc.footer} ${cardBase}`}>
          <p className={`font-semibold ${floating ? "text-footnote" : "text-subheadline"} ${baseText}`}>{t("groups.notInGroup")}</p>
        </div>
      ) : (isBlocked || hasBlockedUs) ? (
        <div
          className={`flex-shrink-0 border-t ${fc.footer} ${cardBase} ${
            floating
              ? ""
              : `bg-gradient-to-r ${isDark ? "from-red-500/10 to-transparent" : "from-red-50 to-transparent"}`
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p
                className={`font-semibold ${floating ? "mb-0.5 text-footnote" : "mb-1 text-subheadline"} ${
                "text-status-danger"
              }`}>
                {isBlocked ? t("messages.youBlockedUser") || "You blocked this user" : t("messages.userBlockedYou") || "This user blocked you"}
              </p>
              <p className={`text-footnote ${
                "text-status-danger"
              }`}>
                {isBlocked ? t("messages.cannotSendBlocked") || "You cannot send messages to this user" : t("messages.cannotSend") || "You cannot send messages"}
              </p>
            </div>
            {isBlocked && (
              <button
                onClick={handleUnblockUser}
                className={`flex items-center gap-1.5 rounded-radius-xs font-semibold transition-all sm:gap-2 sm:rounded-radius-sm ${
                  floating
                    ? `ml-2 shrink-0 px-2 py-1.5 text-xs ${
                        isDark
                          ? "border border-status-success bg-status-success-bg text-status-success hover:bg-green-500/30"
                          : "border border-status-success bg-status-success-bg text-status-success hover:bg-green-100"
                      }`
                    : `ml-4 gap-2 px-4 py-2.5 text-sm ${
                        isDark
                          ? "border border-status-success bg-status-success-bg text-status-success hover:bg-green-500/30"
                          : "border border-status-success bg-status-success-bg text-status-success hover:bg-green-100"
                      }`
                }`}
              >
                <FiUnlock className={floating ? "h-3.5 w-3.5" : "h-4 w-4"} />
                {t("messages.unblock") || "Unblock"}
              </button>
            )}
          </div>
        </div>
      ) : requestStatus === "received" || requestStatus === "youDeclined" ? (
        <div
          className={`flex-shrink-0 border-t ${fc.footer} ${cardBase} ${
            floating
              ? ""
              : ""
          }`}
        >
          <p className={`font-semibold ${floating ? "mb-0.5 text-footnote" : "mb-1 text-subheadline"} ${baseText}`}>
            {requestStatus === "received"
              ? t("messages.requestIncoming", { name: otherName })
              : t("messages.requestYouDeclined")}
          </p>
          <p className={`text-footnote ${floating ? "mb-2" : "mb-3"} ${mutedText}`}>
            {requestStatus === "received"
              ? t("messages.requestIncomingHint")
              : t("messages.requestYouDeclinedHint")}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleAnswerRequest("accept")}
              disabled={answeringRequest}
              className={`flex flex-1 items-center justify-center gap-2 rounded-radius-sm bg-accent-default font-semibold text-accent-on transition disabled:opacity-50 ${
                floating ? "px-2 py-1.5 text-footnote" : "px-4 py-2.5 text-subheadline"
              }`}
            >
              <FiCheck className={floating ? "h-3.5 w-3.5" : "h-4 w-4"} />
              {t("messages.accept")}
            </button>
            {requestStatus === "received" && (
              <button
                type="button"
                onClick={() => handleAnswerRequest("reject")}
                disabled={answeringRequest}
                className={`flex flex-1 items-center justify-center gap-2 rounded-radius-sm border font-semibold transition disabled:opacity-50 ${
                  floating ? "px-2 py-1.5 text-footnote" : "px-4 py-2.5 text-subheadline"
                } ${
                  "border-separator-default bg-bg-raised text-status-danger hover:border-status-danger"
                }`}
              >
                <FiX className={floating ? "h-3.5 w-3.5" : "h-4 w-4"} />
                {t("messages.reject")}
              </button>
            )}
          </div>
        </div>
      ) : requestStatus === "declined" ? (
        <div
          className={`flex-shrink-0 border-t ${fc.footer} ${cardBase} ${
            floating
              ? ""
              : ""
          }`}
        >
          <p className={`font-semibold ${floating ? "mb-0.5 text-footnote" : "mb-1 text-subheadline"} ${baseText}`}>
            {t("messages.requestDeclinedByThem", { name: otherName })}
          </p>
          <p className={`text-footnote ${mutedText}`}>{t("messages.requestDeclinedHint")}</p>
        </div>
      ) : (
        <form
          onSubmit={handleSendMessage}
          className={`flex-shrink-0 border-t ${fc.footer} ${cardBase} ${
            floating
              ? ""
              : ""
          }`}
        >
          {requestStatus === "sent" && (
            <p className={`${floating ? "mb-1.5 text-caption-1" : "mb-3 text-footnote"} ${mutedText}`}>
              {t("messages.requestWaiting", { name: otherName })}
            </p>
          )}
          <ChatPendingFiles
            items={chatFiles.pending}
            onRemove={chatFiles.remove}
            isDark={isDark}
            busy={sending}
            compact={floating}
          />
          <div className={`relative flex items-center ${fc.formRow}`}>
            <MentionSuggestions
              people={mentionPeople}
              active={Math.min(mentionActive, Math.max(mentionPeople.length - 1, 0))}
              onPick={pickMention}
              onHover={setMentionActive}
              isDark={isDark}
              compact={floating}
            />
            <ChatAttachMenu
              onPick={(picked) => {
                chatFiles.addFiles(picked);
                inputRef.current?.focus();
              }}
              isDark={isDark}
              disabled={sending || !connected}
              compact={floating}
              inField={floating}
              className={fc.attach}
            />
            <div className="relative" ref={inputEmojiPickerRef}>
              <button
                type="button"
                onClick={() => setEmojiPickerOpen(!emojiPickerOpen)}
                className={`${fc.emojiBtn} rounded-radius-sm transition-all ${
                  emojiPickerOpen
                    ? isDark
                      ? "bg-white/20"
                      : "bg-slate-200"
                    : "hover:bg-fill-quaternary"
                }`}
              >
                <FiSmile className={`${fc.emojiIcon} ${baseText}`} />
              </button>
              {emojiPickerOpen && (
                <div className={`absolute bottom-full mb-2 left-0 p-3 rounded-radius-sm border shadow-2xl z-50 min-w-[200px] ${
                  "bg-bg-raised border-separator-default"
                }`}>
                  <div className="grid grid-cols-4 gap-2">
                    {commonEmojis.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => {
                          setNewMessage(newMessage + emoji);
                          setEmojiPickerOpen(false);
                          inputRef.current?.focus();
                        }}
                        className="text-2xl hover:scale-125 transition-transform p-2 flex items-center justify-center"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => {
                setNewMessage(e.target.value);
                trackMention(e.target);
                setMentionActive(0);
                if (e.target.value.trim() && !typing) {
                  handleTypingChange(true);
                } else if (!e.target.value.trim() && typing) {
                  handleTypingChange(false);
                }
              }}
              onClick={(e) => trackMention(e.target)}
              onKeyUp={(e) => {
                if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) trackMention(e.target);
              }}
              onKeyDown={(e) => {
                // While members are suggested, the keys pick one rather than send.
                if (mentionPeople.length && !e.nativeEvent?.isComposing) {
                  const count = mentionPeople.length;
                  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                    e.preventDefault();
                    setMentionActive((i) => (Math.min(i, count - 1) + (e.key === "ArrowDown" ? 1 : count - 1)) % count);
                    return;
                  }
                  if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    pickMention(mentionPeople[Math.min(mentionActive, count - 1)]);
                    return;
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setMention(null);
                    return;
                  }
                }
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              onPaste={(e) => {
                const pasted = filesFrom(e.clipboardData);
                if (!pasted.length) return;
                e.preventDefault();
                chatFiles.addFiles(pasted);
              }}
              onBlur={() => {
                setMention(null);
                if (typing) {
                  handleTypingChange(false);
                }
              }}
              placeholder={
                hasPending
                  ? t("chatFiles.addCaption")
                  : replyingTo
                    ? t("messages.replyTo", { name: replyingTo.sender.username || replyingTo.sender.businessName })
                    : t(floating ? "messages.messagePlaceholder" : "messages.typeMessage")
              }
              className={`min-w-0 flex-1 ${inputBase} ${fc.input}`}
              disabled={sending || !connected}
            />
            {(!floating || canSend) && (
              <button
                type="submit"
                disabled={!canSend}
                className={`${fc.sendBtn} transition-colors ${
                  canSend ? "bg-accent-default text-accent-on" : "cursor-not-allowed bg-fill-quaternary text-label-tertiary"
                }`}
              >
                <FiSend className={`${fc.sendIcon} ${sending && hasPending ? "animate-pulse" : ""}`} />
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
};

export default ChatWindow;
